import {
  fingerprintSource,
  normaliseRoute,
  normaliseStack,
  scrubText,
  truncateUtf8,
} from '@dcs/contracts';
import type { ClientErrorApp } from '@dcs/contracts';

/**
 * Client error reporter (F7 AC-2.4, T-2.5): the `ErrorReporter` adapter that posts to
 * `POST /api/client-errors`. Pure core: clock, route, transport and connectivity are injected
 * (`createWebErrorReporter` in ./web.ts binds the browser).
 *
 * Behaviour:
 * - dedupes: one error (same app, normalised message and top frame, i.e. the server's fingerprint) is
 *   sent at most once per page session; the server counts occurrences across sessions;
 * - caps: at most `maxPerMinute` (5) reports in any 60 s and `maxPerSession` (30) per page session,
 *   below the API's 10 per minute per address; extra errors are dropped, not queued (a 429, also the
 *   `daily_cap` one, is never retried: the response is not read);
 * - scrubs first: the same `@dcs/contracts` functions the Worker applies (e-mails, phone numbers,
 *   tokens, query strings, user names in paths). The Worker scrubs again, this only keeps personal data
 *   off the wire;
 * - never throws and never retries: reporting an error must not cause another one;
 * - does not respect Do Not Track / Global Privacy Control, on purpose: unlike usage tracking
 *   (track/tracker.ts), a report carries no identifier at all (no device id, no user id, no cookie, the
 *   IP is not stored), is scrubbed, grouped and deleted after 30 days, and exists to fix the product.
 *   It is skipped while the browser is offline.
 */

/** Reports per rolling 60 s window. */
export const MAX_REPORTS_PER_MINUTE = 5;
/** Reports per page session (module instance). */
export const MAX_REPORTS_PER_SESSION = 30;

const WINDOW_MS = 60_000;
/** Characters kept of a message before the Worker normalises it to 1 KB. */
const MESSAGE_MAX_BYTES = 1024;

/** Posts one JSON body. Returns normally on any outcome. */
export type ErrorSend = (url: string, body: string) => void;

/** Where the error came from; structurally the app's `ErrorContext`. */
export interface ErrorReportContext {
  source?: string;
  componentStack?: string | null | undefined;
}

export interface ErrorReporterPort {
  report(error: unknown, context?: ErrorReportContext): void;
}

export interface ErrorReporterOptions {
  app: ClientErrorApp;
  /** Full URL or same-origin path of `POST /api/client-errors`. */
  endpoint: string;
  /** Current route; only its path is sent (query string and fragment are dropped). */
  route: () => string;
  /** Epoch ms. */
  now: () => number;
  send: ErrorSend;
  /** `false` = drop (offline). Default: online. */
  online?: () => boolean;
  maxPerMinute?: number;
  maxPerSession?: number;
}

interface Described {
  message: string;
  stack: string | undefined;
}

const text = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

function read(source: unknown, key: string): unknown {
  try {
    return typeof source === 'object' && source !== null
      ? (source as Record<string, unknown>)[key]
      : undefined;
  } catch {
    return undefined;
  }
}

/** Message and stack of whatever was thrown (an `Error`, a string, a rejection reason ...). */
function describe(error: unknown, context: ErrorReportContext | undefined): Described {
  let message = text(error) ?? text(read(error, 'message'));
  if (message === undefined) message = `Non-error thrown (${typeof error})`;
  const name = text(read(error, 'name'));
  if (name !== undefined && name !== 'Error' && !message.startsWith(name)) {
    message = `${name}: ${message}`;
  }
  // A React render error has its component stack in the context; the JS stack is preferred.
  const stack = text(read(error, 'stack')) ?? context?.componentStack ?? undefined;
  return { message, stack: stack ?? undefined };
}

export function createErrorReporter(options: ErrorReporterOptions): ErrorReporterPort {
  const { app, endpoint, route, now, send } = options;
  const online = options.online ?? (() => true);
  const perMinute = options.maxPerMinute ?? MAX_REPORTS_PER_MINUTE;
  const perSession = options.maxPerSession ?? MAX_REPORTS_PER_SESSION;

  const seen = new Set<string>();
  let sentInSession = 0;
  let windowStart = 0;
  let windowCount = 0;

  return {
    report(error, context) {
      try {
        if (!online()) return;
        const described = describe(error, context);
        const message = truncateUtf8(scrubText(described.message).trim(), MESSAGE_MAX_BYTES);
        if (message === '') return;
        const key = fingerprintSource(app, message, described.stack);
        if (seen.has(key)) return;

        if (sentInSession >= perSession) return;
        const t = now();
        if (t - windowStart >= WINDOW_MS || t < windowStart) {
          windowStart = t;
          windowCount = 0;
        }
        if (windowCount >= perMinute) return;

        // Marked only once it will really be sent: a dropped error may be reported later.
        seen.add(key);
        windowCount += 1;
        sentInSession += 1;

        const stack = normaliseStack(described.stack);
        send(
          endpoint,
          JSON.stringify({
            app,
            message,
            ...(stack === '' ? {} : { stack }),
            route: normaliseRoute(route()),
          }),
        );
      } catch {
        // Reporting must never throw.
      }
    },
  };
}
